/**
 * computeTemplatePageBreaks — TPN PDF/print parity (2026-05-21).
 *
 * Derives which section wrappers need `page-break-before` in PDF export
 * and print so Puppeteer matches Template Studio TPN physics
 * (`startMode`, `pagePin`, `section.page`, `pageLayouts`).
 *
 * Physical break rules (distinct from logical page-map cursor used for TOC):
 *   - `page-pin`  → break when `startPage > prev.endPage` (new page or whitespace)
 *   - `continue`  → never break
 *   - `auto`      → break only on whitespace gap (`startPage > prev.endPage + 1`)
 *   - `section.page >= 2` → break before first section assigned to that page
 */
import type { ReportTypeId } from '../../config/reportTypes';
import {
  getEffectiveSections,
  type ReportTemplate,
  type SectionConfig,
} from '../../types/reportTemplate';
import {
  resolvePageMap,
  type PageMap,
  type PageMapOverride,
  type ResolverSection,
} from './resolvePageMap';

export interface TemplatePageBreakPlan {
  /** Section ids that receive `data-template-page-break="true"` in export. */
  breakBeforeSectionIds: Set<string>;
  /**
   * FILL-HIERARCHY — the subset of breakBeforeSectionIds NOT backed by an
   * explicit pageMapOverrides entry (type/template defaults). Advisory: the
   * paged engine may suppress one under the strict no-re-strand proof when
   * its donor page re-flows mostly blank. User/plan-pinned breaks (with an
   * override entry) are never suppressed.
   */
  defaultOriginBreakIds: Set<string>;
  pageMap: PageMap;
  printColumns: 1 | 2;
  /** False when no template is supplied. */
  enabled: boolean;
}

export function buildResolverSectionsFromOrder(
  template: ReportTemplate,
  reportType: ReportTypeId,
  sectionOrder: readonly string[],
  visibility: Record<string, boolean> | undefined,
): ResolverSection[] {
  const effective = getEffectiveSections(template, reportType);
  const byId = new Map(effective.map(s => [s.sectionId, s]));
  const result: ResolverSection[] = [];
  let orderIdx = 0;
  for (const id of sectionOrder) {
    const cfg = byId.get(id);
    if (!cfg) continue;
    const vis = visibility?.[id];
    if (vis === false || cfg.visible === false) continue;
    result.push({
      sectionId: id,
      visible: true,
      order: orderIdx++,
      startMode: cfg.startMode ?? 'auto',
      pagePin: cfg.pagePin,
      estimatedPages: cfg.estimatedPages,
    });
  }
  return result;
}

export function breakBeforeIdsFromPageMap(
  pageMap: PageMap,
  sectionById: Map<string, SectionConfig>,
): Set<string> {
  const breaks = new Set<string>();
  for (let i = 1; i < pageMap.sections.length; i++) {
    const prev = pageMap.sections[i - 1];
    const curr = pageMap.sections[i];
    const cfg = sectionById.get(curr.id);
    const mode = cfg?.startMode ?? 'auto';

    if (mode === 'continue') continue;

    if (mode === 'page-pin') {
      if (curr.startPage > prev.endPage) breaks.add(curr.id);
      continue;
    }

    // auto — flowing sections share pages; break only on explicit whitespace.
    if (curr.startPage > prev.endPage + 1) {
      breaks.add(curr.id);
    }
  }
  return breaks;
}

export function breakBeforeIdsFromSectionPage(
  sections: SectionConfig[],
  sectionOrder: readonly string[],
  pageCount: number,
): Set<string> {
  const byId = new Map(sections.map(s => [s.sectionId, s]));
  const firstOnPage = new Map<number, string>();
  for (const id of sectionOrder) {
    const cfg = byId.get(id);
    if (!cfg || cfg.visible === false) continue;
    if (typeof cfg.page === 'number' && cfg.page >= 2 && cfg.page <= pageCount) {
      if (!firstOnPage.has(cfg.page)) firstOnPage.set(cfg.page, id);
    }
  }
  return new Set(firstOnPage.values());
}

export function canonicalSectionId(domId: string): string {
  return domId.split('-')[0];
}

/** Stamp / scrub `data-template-page-break` on live report DOM (export path). */
export function stampTemplatePageBreaks(
  root: HTMLElement,
  breakBeforeSectionIds: Set<string>,
): void {
  root.querySelectorAll('[data-template-page-break]').forEach(el => {
    el.removeAttribute('data-template-page-break');
  });
  if (breakBeforeSectionIds.size === 0) return;

  const sections = Array.from(root.querySelectorAll('[data-section]'));
  for (const node of sections) {
    const id = node.getAttribute('data-section') ?? '';
    if (id === 'cover') continue;
    const canonical = canonicalSectionId(id);
    if (breakBeforeSectionIds.has(id) || breakBeforeSectionIds.has(canonical)) {
      node.setAttribute('data-template-page-break', 'true');
    }
  }
}

export function computeTemplatePageBreakPlan(input: {
  template: ReportTemplate | null | undefined;
  reportType: ReportTypeId;
  sectionOrder: readonly string[];
  sectionVisibility?: Record<string, boolean>;
  pageMapOverrides?: Record<string, PageMapOverride>;
  measuredPages?: Record<string, number>;
}): TemplatePageBreakPlan {
  const emptyPlan: TemplatePageBreakPlan = {
    breakBeforeSectionIds: new Set(),
    defaultOriginBreakIds: new Set(),
    pageMap: { sections: [], totalPages: 0 },
    printColumns: 1,
    enabled: false,
  };
  if (!input.template) return emptyPlan;

  const effectiveSections = getEffectiveSections(input.template, input.reportType);
  const sectionById = new Map(effectiveSections.map(s => [s.sectionId, s]));
  const resolverInput = buildResolverSectionsFromOrder(
    input.template,
    input.reportType,
    input.sectionOrder,
    input.sectionVisibility,
  );
  const pageMap = resolvePageMap(resolverInput, {
    pageMapOverrides: input.pageMapOverrides,
    measuredPages: input.measuredPages,
  });

  const fromMap = breakBeforeIdsFromPageMap(pageMap, sectionById);
  // `|| 1`, not `?? 1`: an EMPTY pageLayouts array has length 0, and `0 ?? 1`
  // is 0 — which makes breakBeforeIdsFromSectionPage's `page <= pageCount` reject
  // every assigned page and silently drop all section-page breaks.
  const pageCount = input.template.pageLayouts?.length || 1;
  const fromSectionPage = breakBeforeIdsFromSectionPage(
    effectiveSections,
    input.sectionOrder,
    pageCount,
  );

  const breakBeforeSectionIds = new Set([...fromMap, ...fromSectionPage]);
  const printColumns: 1 | 2 = input.template.pageLayouts?.[0]?.columns === 2 ? 2 : 1;

  // FILL-HIERARCHY (P-GROUP, 2026-07-03) — origin split. Break ids backed by
  // an EXPLICIT pageMapOverrides entry are USER/PLAN-pinned (binding: the
  // user's own choice or an AI-plan pin folded as a floor). Every other break
  // is TYPE/TEMPLATE-DEFAULT — a heuristic that predates the engine fill
  // policy and may leave a mostly-blank donor page. Default-origin breaks are
  // ADVISORY: the paged engine may suppress one under the strict no-re-strand
  // geometry proof (see PaginatedReportView's verify-and-prune) when its donor
  // page re-flows >50% empty. (Live: public-sentiment's default new-page
  // startModes left 64%-empty donors once the undisplayed-ghost fix made
  // template breaks effective again.)
  const overrideKeys = new Set(Object.keys(input.pageMapOverrides ?? {}));
  const defaultOriginBreakIds = new Set(
    [...breakBeforeSectionIds].filter((id) => !overrideKeys.has(id) && !overrideKeys.has(canonicalSectionId(id))),
  );

  return {
    breakBeforeSectionIds,
    defaultOriginBreakIds,
    pageMap,
    printColumns,
    enabled: true,
  };
}

/** Result shape consumed by SiteRagReportView export wiring. */
export interface TemplatePageBreakResult {
  breakBeforeSectionIds: Set<string>;
  pageMap: PageMap;
  columnCount: 1 | 2;
  active: boolean;
}

/** Entry point for PDF/print export — wraps {@link computeTemplatePageBreakPlan}. */
export function computeTemplatePageBreaks(
  template: ReportTemplate | null,
  opts: {
    reportType: ReportTypeId;
    sectionOrder?: readonly string[];
    sectionVisibility?: Record<string, boolean>;
    pageMapOverrides?: Record<string, PageMapOverride>;
    measuredPages?: Record<string, number>;
  },
): TemplatePageBreakResult {
  const plan = computeTemplatePageBreakPlan({
    template: template ?? undefined,
    reportType: opts.reportType,
    sectionOrder: opts.sectionOrder ?? [],
    sectionVisibility: opts.sectionVisibility,
    pageMapOverrides: opts.pageMapOverrides,
    measuredPages: opts.measuredPages,
  });
  return {
    breakBeforeSectionIds: plan.breakBeforeSectionIds,
    pageMap: plan.pageMap,
    columnCount: plan.printColumns,
    active: plan.enabled,
  };
}
